import { useEffect, useRef, useState } from "react";
import { Images, Folder, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/* Ersetzt window.prompt/confirm in der Cloud: ein Host, Promise-basiert. cloudDialog.confirm({...}) → true/false · prompt({...}) → string|null · newFolder({...}) → {name, galerie}|null */
let _open = null;
const ask = (spec) => new Promise((resolve) => (_open ? _open({ ...spec, resolve }) : resolve(spec.kind === "confirm" ? window.confirm(`${spec.title}\n\n${spec.text || ""}`) : null)));  // ohne Host: nativer Dialog statt stiller Abbruch
export const cloudDialog = {
  confirm: (spec) => ask({ kind: "confirm", ...spec }),
  prompt: (spec) => ask({ kind: "prompt", ...spec }),
  newFolder: (spec) => ask({ kind: "newFolder", ...spec }),
};

/* Einheitliche Lösch-/Stopp-Bestätigung: Ersatz für window.confirm(text) – erste Fragezeile wird Titel, Verb bestimmt Gefahr + Button-Text. */
const VERBEN = ["endgültig löschen", "löschen", "entfernen", "stoppen", "leeren", "trennen", "widerrufen", "kündigen", "absagen", "stornieren", "zurückziehen", "verwerfen", "abbrechen", "beenden", "aufheben", "entkoppeln"];
export const appConfirm = (text, opts = {}) => {
  const s = String(text || "").trim();
  const [erste, ...rest] = s.split("\n");
  const istFrage = erste.trim().length <= 90 && /\?\s*$/.test(erste);
  const title = istFrage ? erste.trim() : "Bist du sicher?";
  const body = istFrage ? rest.join("\n").trim() : s;
  const verb = VERBEN.find((v) => erste.toLowerCase().includes(v));
  return cloudDialog.confirm({ title, text: body, ok: verb ? `Ja, ${verb}` : "Ja, weiter", danger: !!verb, ...opts });
};

export const CloudDialogHost = () => {
  const [d, setD] = useState(null);
  useEffect(() => { _open = (spec) => setD(spec); return () => { _open = null; }; }, []);
  const close = (val) => { d?.resolve(val); setD(null); };
  if (!d) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && close(d.kind === "confirm" ? false : null)}>
      <DialogContent className="max-w-md rounded-3xl border-[#ECE9DF] bg-[#FBFAF5] p-0 overflow-hidden" data-testid={`cloud-dialog-${d.kind}`}>
        {d.kind === "newFolder" ? <NewFolderBody d={d} close={close} /> : d.kind === "prompt" ? <PromptBody d={d} close={close} /> : <ConfirmBody d={d} close={close} />}
      </DialogContent>
    </Dialog>
  );
};

const Head = ({ title, text, icon }) => (
  <DialogHeader className="px-5 pt-6 text-left sm:px-6">
    <DialogTitle className="font-display text-xl text-[#111] inline-flex items-start gap-2 leading-tight">{icon && <span className="mt-0.5 shrink-0">{icon}</span>}{title}</DialogTitle>
    {text && <DialogDescription className="whitespace-pre-line text-sm text-[#5C574F] leading-relaxed">{text}</DialogDescription>}
  </DialogHeader>
);

const Foot = ({ children }) => <DialogFooter className="mt-4 flex-col-reverse gap-2 border-t border-[#ECE9DF] bg-white/60 px-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] pt-4 sm:flex-row sm:justify-end sm:px-6 sm:pb-6 [&>button]:min-h-[48px] [&>button]:w-full sm:[&>button]:w-auto [&>button]:!text-sm [&>button]:!rounded-2xl">{children}</DialogFooter>;

const ConfirmBody = ({ d, close }) => (
  <>
    <Head title={d.title} text={d.text} icon={d.danger ? <AlertTriangle size={20} className="text-[#9B2C2C]" /> : null} />
    <Foot>
      <button type="button" onClick={() => close(false)} className="p-btn p-btn-ghost border border-[#E5E0D4] bg-white" data-testid="cloud-dialog-cancel">{d.cancel || "Abbrechen"}</button>
      <button type="button" autoFocus onClick={() => close(true)} className={`p-btn ${d.danger ? "bg-[#9B2C2C] text-white hover:bg-[#7f2222]" : "p-btn-primary"}`} data-testid="cloud-dialog-ok">{d.ok || "OK"}</button>
    </Foot>
  </>
);

const PromptBody = ({ d, close }) => {
  const [v, setV] = useState(d.value || "");
  const ref = useRef(null);
  const min = d.minLength || 0;
  const ok = v.trim().length >= Math.max(1, min);
  useEffect(() => { ref.current?.focus?.(); ref.current?.select?.(); }, []);
  const submit = (e) => { e.preventDefault(); if (ok) close(v.trim()); };
  const cls = "w-full rounded-xl border border-[#E5E0D4] bg-white px-3 py-2 text-sm text-[#111] focus:outline-none focus:ring-2 focus:ring-[#FDD105]";
  return (
    <form onSubmit={submit}>
      <Head title={d.title} text={d.text} icon={d.danger ? <AlertTriangle size={20} className="text-[#9B2C2C]" /> : null} />
      <div className="px-6 pt-4"><label className="block text-[11px] uppercase tracking-wider text-[#8C867B] mb-1">{d.label || (d.type === "datetime-local" ? "Zeitpunkt" : "Name")}</label>
        {d.multiline
          ? <textarea ref={ref} value={v} onChange={(e) => setV(e.target.value)} rows={4} maxLength={d.maxLength || 400} placeholder={d.placeholder || ""} className={`${cls} min-h-[96px] resize-none`} data-testid="cloud-dialog-input" />
          : <input ref={ref} autoFocus type={d.type || "text"} value={v} onChange={(e) => setV(e.target.value)} maxLength={d.type ? undefined : (d.maxLength || 200)} placeholder={d.placeholder || ""} className={`${cls} min-h-[44px]`} data-testid="cloud-dialog-input" />}
        {min > 0 && <p className="mt-1 text-[11px] text-[#8C867B]" data-testid="cloud-dialog-minlen">{v.trim().length}/{min} Zeichen mindestens</p>}
        {d.note && <p className="mt-3 flex items-start gap-2 rounded-xl bg-[#FFF4D6] px-3 py-2 text-[12px] leading-snug text-[#7A5A00]" data-testid="cloud-dialog-note"><AlertTriangle size={14} className="mt-0.5 shrink-0" />{d.note}</p>}</div>
      <Foot>
        <button type="button" onClick={() => close(null)} className="p-btn p-btn-ghost !text-xs" data-testid="cloud-dialog-cancel">Abbrechen</button>
        <button type="submit" disabled={!ok} className={`p-btn !text-xs disabled:opacity-50 ${d.danger ? "bg-[#9B2C2C] text-white hover:bg-[#7f2222]" : "p-btn-primary"}`} data-testid="cloud-dialog-ok">{d.ok || "Speichern"}</button>
      </Foot>
    </form>
  );
};

/* Neuer Ordner: Name + Art (normal / Galerie) als zwei wählbare Karten statt OK/Abbrechen-Rätsel. */
const NewFolderBody = ({ d, close }) => {
  const [name, setName] = useState("");
  const [galerie, setGalerie] = useState(!!d.galerie);
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); }, []);
  const submit = (e) => { e.preventDefault(); if (name.trim()) close({ name: name.trim(), galerie }); };
  const Opt = ({ on, icon, title, text, testid }) => (
    <button type="button" onClick={() => setGalerie(on)} aria-pressed={galerie === on} data-testid={testid}
      className={`text-left rounded-2xl border-2 p-3 transition-colors ${galerie === on ? "border-[#111] bg-white" : "border-[#E5E0D4] bg-white/50 hover:border-[#8C867B]"}`}>
      <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#111]">{icon}{title}</span>
      <span className="block mt-1 text-xs text-[#5C574F] leading-relaxed">{text}</span>
    </button>
  );
  return (
    <form onSubmit={submit}>
      <Head title={d.title || "Neuer Ordner"} text={d.text || "Gib dem Ordner einen Namen und wähle, ob er eine Galerie sein soll. Beides lässt sich später ändern."} />
      <div className="px-6 pt-4 space-y-4">
        <div><label className="block text-[11px] uppercase tracking-wider text-[#8C867B] mb-1">Name</label>
          <input ref={ref} value={name} onChange={(e) => setName(e.target.value)} maxLength={120} placeholder="z. B. Messe 2026" className="w-full rounded-xl border border-[#E5E0D4] bg-white px-3 py-2 text-sm text-[#111] focus:outline-none focus:ring-2 focus:ring-[#FDD105]" data-testid="cloud-dialog-input" /></div>
        <div className="grid sm:grid-cols-2 gap-2" role="radiogroup" aria-label="Art des Ordners">
          <Opt on={false} icon={<Folder size={15} />} title="Normaler Ordner" text="Dateien wie sie sind – für Unterlagen, Logos, Videos, alles." testid="cloud-dialog-normal" />
          <Opt on icon={<Images size={15} className="text-[#7A8A00]" />} title="Galerie" text="Fotos werden beim Hochladen automatisch aufgehübscht (SocialFix, Original bleibt). Der Freigabe-Link zeigt sie als Galerie mit Lightbox und Download." testid="cloud-dialog-galerie" />
        </div>
        <p className="text-[11px] text-[#8C867B]">Neue Ordner sind privat. Geteilt wird erst, wenn du selbst einen Freigabe-Link erstellst.</p>
      </div>
      <Foot>
        <button type="button" onClick={() => close(null)} className="p-btn p-btn-ghost !text-xs" data-testid="cloud-dialog-cancel">Abbrechen</button>
        <button type="submit" disabled={!name.trim()} className="p-btn p-btn-primary !text-xs disabled:opacity-50" data-testid="cloud-dialog-ok">{galerie ? "Galerie anlegen" : "Ordner anlegen"}</button>
      </Foot>
    </form>
  );
};
